import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  headerOperation,
  cellOperation,
  cellDobleRowOperation,
  cellNumOperation,
} from 'helpers/tables';

import { faEye } from '@fortawesome/free-solid-svg-icons';
import DownloadBtn from 'components/shared/DownloadBtn';
import ClientSideTable from 'components/shared/ClientSideTable';
import { fdate } from 'helpers/helpers';
import { API, BASE_URL } from 'CONST';
import auth from 'helpers/auth';
import TableLink from 'components/shared/TableLink';
import { tiposDeudorToFilterOptions, objectsToFilterOptions } from 'helpers/selects';
import FilterSelect from 'components/shared/FilterSelect';

const ConsolidadoCredito = ({ client }) => {
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiposDeudor, setTiposDeudor] = useState([]);
  const [estadosMovimiento, setEstadosMovimiento] = useState([]);
  const user = useSelector((state) => state.login.user);

  useEffect(() => {
    auth.fetch(`${BASE_URL}${API.tiposDeudor}/`).then((resp) => {
      setTiposDeudor(tiposDeudorToFilterOptions(resp));
    });

    auth.fetch(`${BASE_URL}${API.estadosMovimiento}/`).then((resp) => {
      setEstadosMovimiento(objectsToFilterOptions(resp));
    });

    setLoading(true);
    const url = `${BASE_URL}${API.lineasCredito}/`;
    auth
      .fetch(client ? `${url}?client_id=${client.id}` : url)
      .then((result) => setLineas(result))
      .finally(() => setLoading(false));
  }, [client]);

  const columns = [
    {
      Header: headerOperation('Número de Póliza'),
      id: 'poliza',
      accessor: 'poliza_nemotecnico',
      Cell: (row) => cellOperation(row.value, 'center'),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Deudor'),
      id: 'deudor',
      accessor: 'deudor',
      Cell: (row) => cellDobleRowOperation(row.value.rut, row.value.nombre),
      sortable: false,
      minWidth: 100,
      filterMethod: (filter, row) => {
        const {
          deudor: { nombre, rut },
        } = row;
        return nombre.includes(filter.value) || rut.includes(filter.value);
      },
    },
    {
      Header: headerOperation('Tipo'),
      id: 'tipo_deudor',
      accessor: 'tipo_deudor.descripcion',
      Cell: (row) => cellOperation(row.value, 'center'),
      Filter: (obj) => (
        <FilterSelect
          filter={obj.filter}
          defaultOption={{ label: 'Todos', value: 'Todos' }}
          onFilterChange={obj.onChange}
          options={tiposDeudor}
        />
      ),
      filterMethod: (filter, row) => {
        const { tipo_deudor: tipoDeudor } = row;
        return tipoDeudor !== undefined && filter.value !== 'Todos'
          ? tipoDeudor === filter.value
          : true;
      },
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Monto Otorgado (UF)'),
      id: 'monto_otorgado',
      accessor: 'balance.monto_otorgado',
      Cell: (row) => cellNumOperation(row.value),
      filterable: false,
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Estado'),
      id: 'estado',
      accessor: 'balance.estado.descripcion',
      Cell: (row) => cellOperation(row.value, 'center'),
      Filter: (obj) => (
        <FilterSelect
          filter={obj.filter}
          defaultOption={{ label: 'Todos', value: 'Todos' }}
          onFilterChange={obj.onChange}
          options={estadosMovimiento}
        />
      ),
      filterMethod: (filter, row) => {
        const { estado } = row;
        return estado !== undefined && filter.value !== 'Todos' ? estado === filter.value : true;
      },
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Fecha Vigencia'),
      id: 'fecha_vigencia',
      accessor: 'balance.fecha_vigencia',
      Cell: (row) => cellOperation(row.value),
      filterMethod: (filter, row) => fdate(row.fecha_vigencia).includes(filter.value),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Acciones', 'center'),
      id: 'actions',
      accessor: (i) => (
        <div style={{ textAlign: 'center' }}>
          <TableLink
            title="ver"
            url={`/asegurado/lineas-credito/detalle/?poliza_id=${i.poliza_id}&linea_id=${i.id}`}
            icon={faEye}
          />
        </div>
      ),
      filterable: false,
      sortable: false,
      width: 100,
    },
  ];

  const xlsDownloadUrl = `${BASE_URL}${API.lineasCredito}/xlsx/`;
  const pdfDownloadUrl = `${BASE_URL}${API.lineasCredito}/pdf/`;

  return (
    <>
      {!user.is_orsan && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '65px 400px',
            gridGap: '3px 8px',
            alignItems: 'center',
            padding: '0 0 16px 16px',
          }}
        >
          <div className="orsan-label">Nombre:</div>
          <div className="orsan-value">{user.organization_name}</div>
          <div className="orsan-label">RUT:</div>
          <div className="orsan-value">{user.organization_rut}</div>
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gridGap: '10px',
          marginBottom: '6px',
        }}
      >
        <div style={{ gridColumnStart: 2 }}>
          <DownloadBtn
            id="xlsx-download"
            text="XLSX"
            tipText="Descargar Planilla Excel"
            url={client ? `${xlsDownloadUrl}?client_id=${client.id}` : xlsDownloadUrl}
          />
        </div>
        <div style={{ gridColumnStart: 3 }}>
          <DownloadBtn
            id="pdf-download"
            text="PDF"
            tipText="Descargar PDF"
            url={client ? `${pdfDownloadUrl}?client_id=${client.id}` : pdfDownloadUrl}
          />
        </div>
      </div>
      <ClientSideTable
        columns={columns}
        data={lineas}
        pageSize={20}
        loading={loading}
        style={{ maxHeight: '63vh' }}
      />
    </>
  );
};

ConsolidadoCredito.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

ConsolidadoCredito.defaultProps = {
  client: null,
};

export default ConsolidadoCredito;
